import { Icon } from "@iconify/react"
import type { IDisplaySection } from "@lexicanon/shared"
import { List } from "@mantine/core"
import { useProduct } from "../../../lib/tenant"
import { EditableAnalysisField } from "../../EditableAnalysisField"
import type { IEditContext } from "../interfaces"
import styles from "./LexSection.module.css"
import { SectionHeader } from "./SectionHeader"

interface IProps {
  section: IDisplaySection
  data: unknown
  editContext?: IEditContext
}

// Sections that benefit from numbered styling
const NUMBERED_SECTIONS = new Set(["decisions"])
// Sections that use a question-mark icon
const QUESTION_SECTIONS = new Set(["open_questions"])

export function ListSection({ section, data, editContext }: IProps): React.ReactElement | null {
  const product = useProduct()
  if (!Array.isArray(data) || data.length === 0) return null

  if (product === "lexicanon") {
    if (NUMBERED_SECTIONS.has(section.id)) {
      return (
        <div>
          <SectionHeader id={section.id} label={section.label} />
          <div className={styles.itemList}>
            {data.map((item, i) => {
              const text = typeof item === "string" ? item : formatObject(item)
              const fieldPath = `${section.dataPath}.${i}`
              return (
                <div key={`${section.id}-${text}`} className={styles.numberedItem}>
                  <span className={styles.numberBadge}>{i + 1}</span>
                  <span className={styles.itemText}>
                    {editContext && typeof item === "string" ? (
                      <EditableAnalysisField
                        meetingId={editContext.meetingId}
                        fieldPath={fieldPath}
                        value={text}
                        canEdit={editContext.canEdit}
                        isEdited={!!editContext.fieldEdits[fieldPath]}
                        onSaved={editContext.onEdited}
                      />
                    ) : (
                      text
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )
    }

    if (QUESTION_SECTIONS.has(section.id)) {
      return (
        <div>
          <SectionHeader id={section.id} label={section.label} />
          <div className={styles.itemList}>
            {data.map((item, i) => {
              const text = typeof item === "string" ? item : formatObject(item)
              const fieldPath = `${section.dataPath}.${i}`
              return (
                <div key={`${section.id}-${text}`} className={styles.numberedItem}>
                  <Icon icon="tabler:question-mark" width={14} height={14} className={styles.questionIcon} />
                  <span className={styles.itemText}>
                    {editContext && typeof item === "string" ? (
                      <EditableAnalysisField
                        meetingId={editContext.meetingId}
                        fieldPath={fieldPath}
                        value={text}
                        canEdit={editContext.canEdit}
                        isEdited={!!editContext.fieldEdits[fieldPath]}
                        onSaved={editContext.onEdited}
                      />
                    ) : (
                      text
                    )}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
  }

  return (
    <div>
      <SectionHeader id={section.id} label={section.label} />
      <List size="sm">
        {data.map((item, i) => {
          const text = typeof item === "string" ? item : formatObject(item)
          const fieldPath = `${section.dataPath}.${i}`
          return (
            <List.Item key={`${section.id}-${text}`}>
              {editContext && typeof item === "string" ? (
                <EditableAnalysisField
                  meetingId={editContext.meetingId}
                  fieldPath={fieldPath}
                  value={text}
                  canEdit={editContext.canEdit}
                  isEdited={!!editContext.fieldEdits[fieldPath]}
                  onSaved={editContext.onEdited}
                />
              ) : (
                text
              )}
            </List.Item>
          )
        })}
      </List>
    </div>
  )
}

function isRecord(val: unknown): val is Record<string, unknown> {
  return typeof val === "object" && val !== null && !Array.isArray(val)
}

function formatObject(obj: unknown): string {
  if (!isRecord(obj)) return String(obj)
  if (obj.description) return `${obj.name ?? obj.owner ?? ""}: ${obj.description}`.trim().replace(/^:\s*/, "")
  if (obj.item) return `${obj.item}${obj.recipient ? ` → ${obj.recipient}` : ""}`
  return Object.values(obj).filter(Boolean).join(" — ")
}
